// Simple cache utility: in-memory map + localStorage persistence with TTL (ms)
export type CacheEntry<T> = { value: T; expiresAt: number };

const MEMORY_CACHE: Map<string, CacheEntry<any>> = new Map();

export function setCache<T>(key: string, value: T, ttlSeconds = 300) {
  const expiresAt = Date.now() + ttlSeconds * 1000;
  const entry: CacheEntry<T> = { value, expiresAt };
  MEMORY_CACHE.set(key, entry);
  try {
    localStorage.setItem(`ss_cache:${key}`, JSON.stringify(entry));
  } catch {
    // ignore localStorage errors (e.g., quota, SSR)
  }
}

export function getCache<T = any>(key: string): T | null {
  const now = Date.now();
  const mem = MEMORY_CACHE.get(key) as CacheEntry<T> | undefined;
  if (mem) {
    if (mem.expiresAt > now) return mem.value;
    MEMORY_CACHE.delete(key);
  }

  try {
    const raw = localStorage.getItem(`ss_cache:${key}`);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as CacheEntry<T> | null;
    if (!parsed) return null;
    if (parsed.expiresAt > now) {
      MEMORY_CACHE.set(key, parsed);
      return parsed.value;
    }
    // expired
    localStorage.removeItem(`ss_cache:${key}`);
    return null;
  } catch {
    return null;
  }
}

export function clearCache(key: string) {
  MEMORY_CACHE.delete(key);
  try {
    localStorage.removeItem(`ss_cache:${key}`);
  } catch {}
}

export function clearAllCache() {
  MEMORY_CACHE.clear();
  try {
    Object.keys(localStorage).forEach((k) => {
      if (k.startsWith('ss_cache:')) localStorage.removeItem(k);
    });
  } catch {}
}

const cacheUtils = { getCache, setCache, clearCache, clearAllCache };
export default cacheUtils;
